import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AdSlot } from "@/components/AdSlot";
import { LogOut, HardHat, ClipboardList, Users, Home } from "lucide-react";

export default function DashboardLayout({ title, subtitle, tabs, active, onTab, children }) {
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col bg-slate-950 text-slate-300 fixed h-screen">
        <div className="px-6 py-6 border-b border-slate-800 flex items-center gap-2">
          <div className="w-9 h-9 bg-orange-500 flex items-center justify-center rounded-sm">
            <HardHat className="w-5 h-5 text-white" />
          </div>
          <span className="font-display text-lg font-extrabold text-white tracking-tight">
            Work4you
          </span>
        </div>
        <nav className="flex-1 py-4 space-y-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              data-testid={`nav-${t.key}`}
              onClick={() => onTab(t.key)}
              className={`w-full flex items-center gap-3 px-6 py-3 text-sm font-medium transition-colors duration-200 ${
                active === t.key
                  ? "bg-orange-500 text-white"
                  : "text-slate-400 hover:bg-slate-900 hover:text-white"
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-800">
          {user?.role !== "admin" && (
            <div className="mb-4">
              <AdSlot slot="sidebar" rol={user?.role} provincie={user?.provincie} />
            </div>
          )}
          <div className="px-2 mb-3">
            <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
          <Button
            data-testid="logout-btn"
            onClick={logout}
            variant="outline"
            className="w-full bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white rounded-sm"
          >
            <LogOut className="w-4 h-4 mr-2" /> Uitloggen
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 md:ml-64">
        {/* Mobile tabs */}
        <div className="md:hidden flex items-center justify-between bg-slate-950 text-white px-4 py-3">
          <span className="font-display font-extrabold">Work4you</span>
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="dash-mobile-home" title="Home" className="text-orange-400">
              <Home className="w-5 h-5" />
            </Link>
            <Link to="/opdrachten" data-testid="dash-mobile-opdrachten" title="Opdrachten" className="text-blue-300">
              <ClipboardList className="w-5 h-5" />
            </Link>
            <Link to="/beschikbare_monteurs" data-testid="dash-mobile-monteurs" title="Professionals" className="text-emerald-300">
              <Users className="w-5 h-5" />
            </Link>
            <button data-testid="mobile-logout-btn" onClick={logout}>
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="md:hidden flex overflow-x-auto bg-slate-900 text-slate-300">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => onTab(t.key)}
              className={`flex-shrink-0 px-4 py-2 text-xs font-semibold ${
                active === t.key ? "text-orange-400 border-b-2 border-orange-400" : "text-slate-400"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <header className="bg-white border-b border-slate-200 px-6 lg:px-10 py-6 flex items-start justify-between gap-4">
          <div>
            <p className="label-caps mb-1">{subtitle}</p>
            <h1 className="font-display text-2xl lg:text-3xl font-extrabold text-slate-950">
              {title}
            </h1>
          </div>
          <div className="hidden sm:flex items-center gap-2 shrink-0">
            <Link to="/" data-testid="dash-nav-home">
              <Button variant="ghost" className="rounded-sm font-semibold gap-2 text-slate-700 hover:text-orange-600">
                <Home className="w-4 h-4" /> Home
              </Button>
            </Link>
            <Link to="/opdrachten" data-testid="dash-nav-opdrachten">
              <Button variant="ghost" className="rounded-sm font-semibold gap-2 text-slate-700 hover:text-blue-700">
                <ClipboardList className="w-4 h-4" /> Opdrachten
              </Button>
            </Link>
            <Link to="/beschikbare_monteurs" data-testid="dash-nav-monteurs">
              <Button variant="ghost" className="rounded-sm font-semibold gap-2 text-slate-700 hover:text-emerald-700">
                <Users className="w-4 h-4" /> Professionals
              </Button>
            </Link>
          </div>
        </header>
        <div className="p-6 lg:p-10">{children}</div>
      </main>
    </div>
  );
}
